import { supabase } from '../supabase';
import { Website, Link, GraphData, WebsiteWithConnections } from '../../types/website';

// Every column except the three passwords. A login must not read those by asking for the whole row.
export const WEBSITE_COLUMNS =
  'id, url, title, created_at, color, build_status, deployment_status, dns_host, hosting_provider, hosting_username, frontend_platform, frontend_theme, search_console_access, indexing_status, nameservers, frontend_username, registrar, registrar_username, hosting_location, ip_address, last_content_update, category, domain';

export async function fetchAdminWebsiteSecret(id: string): Promise<{
  hosting_password: string | null;
  frontend_password: string | null;
  registrar_password: string | null;
} | null> {
  const { data, error } = await supabase.rpc('admin_website_secret', { p_id: id });
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return row ?? null;
}

// Helper function to safely execute Supabase queries with retry logic
const safeSupabaseQuery = async (queryFn: () => PromiseLike<any>, retries = 3, delay = 1000) => {
  let attempt = 0;
  while (attempt < retries) {
    try {
      return await queryFn();
    } catch (error) {
      console.error(`Supabase query attempt ${attempt + 1} failed:`, error);
      attempt++;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
      // Increase delay for next retry (exponential backoff)
      delay *= 2;
    }
  }
};

// Fetch all websites
export const fetchWebsites = async (): Promise<Website[]> => {
  try {
    const { data, error } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .select(WEBSITE_COLUMNS)
        .order('created_at', { ascending: false })
    );

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching websites:', error);
    throw error;
  }
};

// Fetch all links
export const fetchLinks = async (): Promise<Link[]> => {
  try {
    const { data, error } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .select('*')
        .order('created_at', { ascending: false })
    );

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching links:', error);
    throw error;
  }
};

// Fetch only websites that have connections (either incoming or outgoing links)
export const fetchConnectedGraphData = async (): Promise<GraphData> => {
  try {
    // First get all links
    const { data: links, error: linksError } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .select('*')
    );

    if (linksError) throw linksError;
    
    // Extract unique website IDs that have connections
    const connectedWebsiteIds = new Set<string>();
    (links as Link[]).forEach((link) => {
      connectedWebsiteIds.add(link.from_id);
      connectedWebsiteIds.add(link.to_id);
    });
    
    // Fetch only the websites that have connections
    const { data: websites, error: websitesError } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .select(WEBSITE_COLUMNS)
        .in('id', Array.from(connectedWebsiteIds))
    );

    if (websitesError) throw websitesError;

    // Transform websites to nodes
    const nodes = (websites as Website[]).map((website) => ({
      id: website.id,
      url: website.url,
      title: website.title,
      color: website.color || '#d95636'
    }));

    // Transform links to match force graph format
    const graphLinks = (links as Link[]).map((link) => ({
      source: link.from_id,
      target: link.to_id
    }));

    return { nodes, links: graphLinks };
  } catch (error) {
    console.error('Error fetching connected graph data:', error);
    throw error;
  }
};

// Fetch graph data (nodes and links)
export const fetchGraphData = async (): Promise<GraphData> => {
  try {
    const [websites, links] = await Promise.all([
      fetchWebsites(),
      fetchLinks()
    ]);

    // Transform websites to nodes
    const nodes = (websites as Website[]).map((website) => ({
      id: website.id,
      url: website.url,
      title: website.title,
      color: website.color || '#d95636'
    }));

    // Transform links to match force graph format
    const graphLinks = (links as Link[]).map((link) => ({
      source: link.from_id,
      target: link.to_id
    }));

    return { nodes, links: graphLinks };
  } catch (error) {
    console.error('Error fetching graph data:', error);
    throw error;
  }
};

// Add a new website
export const addWebsite = async (
  url: string, 
  title?: string | null, 
  additionalData?: Partial<Website>
): Promise<Website> => {
  try {
    // Combine basic data with any additional fields
    const websiteData: any = { url, title, ...additionalData };
    
    const { data, error } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .insert([websiteData])
        .select(WEBSITE_COLUMNS)
        .single()
    );

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding website:', error);
    throw error;
  }
};

// Add a new link between websites
export const addLink = async (fromId: string, toId: string): Promise<Link> => {
  try {
    const { data, error } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .insert([{ from_id: fromId, to_id: toId }])
        .select()
        .single()
    );

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding link:', error);
    throw error;
  }
};

// Delete a link
export const deleteLink = async (linkId: string): Promise<void> => {
  try {
    const { error } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .delete()
        .eq('id', linkId)
    );

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting link:', error);
    throw error;
  }
};

// Delete a website
export const deleteWebsite = async (websiteId: string): Promise<void> => {
  try {
    const { error } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .delete()
        .eq('id', websiteId)
    );

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting website:', error);
    throw error;
  }
};

// Get website with connections
export const getWebsiteWithConnections = async (websiteId: string): Promise<WebsiteWithConnections | null> => {
  try {
    // Fetch the website with all fields
    const { data: website, error: websiteError } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .select(WEBSITE_COLUMNS)
        .eq('id', websiteId)
        .single()
    );

    if (websiteError) throw websiteError;
    if (!website) return null;

    // Fetch outgoing links
    const { data: outgoingLinks, error: outgoingError } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .select(`
          id,
          to_id,
          created_at,
          website:websites!links_to_id_fkey(${WEBSITE_COLUMNS})
        `)
        .eq('from_id', websiteId)
        .order('created_at', { ascending: false })
    );

    if (outgoingError) throw outgoingError;

    // Fetch incoming links
    const { data: incomingLinks, error: incomingError } = await safeSupabaseQuery(() => 
      supabase
        .from('links')
        .select(`
          id,
          from_id,
          created_at,
          website:websites!links_from_id_fkey(${WEBSITE_COLUMNS})
        `)
        .eq('to_id', websiteId)
        .order('created_at', { ascending: false })
    );

    if (incomingError) throw incomingError;

    // Combine all data
    return {
      ...website,
      outgoingLinks: outgoingLinks || [],
      incomingLinks: incomingLinks || []
    };
  } catch (error) {
    console.error('Error fetching website with connections:', error);
    throw error;
  }
};

// Assign a website task to a user
export const assignWebsiteTask = async (taskId: string, userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('assign_website_task_to_user', {
      p_website_task_id: taskId,
      p_user_id: userId
    });
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error assigning website task:', error);
    throw error;
  }
};

// Update a website
export const updateWebsite = async (websiteId: string, data: { url?: string; title?: string | null; color?: string }): Promise<Website> => {
  try {
    const { data: updatedWebsite, error } = await safeSupabaseQuery(() => 
      supabase
        .from('websites')
        .update(data)
        .eq('id', websiteId)
        .select(WEBSITE_COLUMNS)
        .single()
    );

    if (error) throw error;
    return updatedWebsite;
  } catch (error) {
    console.error('Error updating website:', error);
    throw error;
  }
};